import { AddOutlined, CallOutlined, CancelOutlined, CheckCircleOutlined, PersonOutlined } from '@mui/icons-material';
import {
  Alert,
  Chip,
  Container,
  Snackbar,
  Typography,
} from '@mui/material';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { useCallback, useMemo, useState } from 'react';
// @ts-ignore
import MicrospecDataTable from '../../../MicrospecDynamicUI/Components/DataTable/MicrospecFirestoreDataTable';
import { useMicrospecDynamicUIContext } from '../../../MicrospecDynamicUI/GlobalProvider';
import AppointmentDialog from './AppointmentDialog';

const TYPE_CONFIG: Record<string, { label: string; color: 'primary' | 'secondary' | 'info' }> = {
  frontal: { label: 'حضوري', color: 'primary' },
  call: { label: 'اتصال', color: 'info' },
};

const STATUS_CONFIG: Record<string, { label: string; color: 'default' | 'success' | 'error' | 'warning' }> = {
  scheduled: { label: 'مجدول', color: 'warning' },
  completed: { label: 'مكتمل', color: 'success' },
  cancelled: { label: 'ملغي', color: 'error' },
};

interface ClientOption {
  id: string;
  first_name: string;
  last_name: string;
}

const ManageAppointments = () => {
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const userProfile = (cloudProvider as any).userProfile?.attributes;
  const firestore = (cloudProvider as any).firestore;
  const [refreshKey, setRefreshKey] = useState(0);

  // Add dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clients, setClients] = useState<ClientOption[]>([]);

  // Snackbar
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  // Load owner's clients for the dialog
  const loadClients = useCallback(async () => {
    if (!firestore?.instance || !userProfile?.uid) return;
    try {
      const db = firestore.instance;
      const snap = await getDocs(collection(db, 'clients'));
      const owned = snap.docs
        .map((d: any) => ({ id: d.id, ...d.data() } as ClientOption))
        .filter((c: any) => c.owner_id === userProfile.uid);
      setClients(owned);
    } catch (e) {
      console.error('Error loading clients', e);
    }
  }, [firestore?.instance, userProfile?.uid]);

  const handleOpenDialog = useCallback(() => {
    loadClients();
    setDialogOpen(true);
  }, [loadClients]);

  const handleApptSuccess = useCallback((message: string) => {
    setRefreshKey((k) => k + 1);
    setSnackMessage(message);
    setSnackOpen(true);
  }, []);

  const handleMarkCompleted = useCallback(async (row: any) => {
    try {
      await updateDoc(doc(firestore.instance, 'appointments', row.id), { status: 'completed' });
      setRefreshKey((k) => k + 1);
      setSnackMessage('تم تحديث الموعد كمكتمل');
      setSnackOpen(true);
    } catch (e) {
      console.error('Error updating appointment', e);
    }
  }, [firestore?.instance]);

  const handleMarkCancelled = useCallback(async (row: any) => {
    try {
      await updateDoc(doc(firestore.instance, 'appointments', row.id), { status: 'cancelled' });
      setRefreshKey((k) => k + 1);
      setSnackMessage('تم إلغاء الموعد');
      setSnackOpen(true);
    } catch (e) {
      console.error('Error updating appointment', e);
    }
  }, [firestore?.instance]);

  const columns = useMemo(() => [
    {
      field: 'client_name',
      headerName: 'العميل',
      minWidth: 160,
    },
    {
      field: 'type',
      headerName: 'نوع الموعد',
      minWidth: 120,
      render: (value: any) => {
        const cfg = TYPE_CONFIG[value] || { label: value, color: 'default' as const };
        return (
          <Chip
            component="span"
            size="small"
            icon={value === 'call' ? <CallOutlined fontSize="small" /> : <PersonOutlined fontSize="small" />}
            label={cfg.label}
            color={cfg.color}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'date_time',
      headerName: 'التاريخ والوقت',
      minWidth: 200,
      render: (value: any) => {
        if (!value) return '-';
        const date = new Date(value?.seconds ? value.seconds * 1000 : value);
        return (
          <Typography variant="body2">
            {date.toLocaleDateString('ar', { year: 'numeric', month: 'long', day: 'numeric' })}
            {' — '}
            {date.toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'الحالة',
      minWidth: 120,
      render: (value: any) => {
        const cfg = STATUS_CONFIG[value] || { label: value, color: 'default' as const };
        return <Chip component="span" size="small" label={cfg.label} color={cfg.color} variant="outlined" />;
      },
    },
    {
      field: 'notes',
      headerName: 'ملاحظات',
      minWidth: 180,
      render: (value: any) => (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
          {value || '—'}
        </Typography>
      ),
    },
  ], []);

  const actions = useMemo(() => [
    {
      icon: <CheckCircleOutlined fontSize="small" color="success" />,
      label: 'مكتمل',
      onClick: handleMarkCompleted,
    },
    {
      icon: <CancelOutlined fontSize="small" color="error" />,
      label: 'ملغي',
      onClick: handleMarkCancelled,
    },
  ], [handleMarkCompleted, handleMarkCancelled]);

  const globalActions = useMemo(() => [
    {
      icon: <AddOutlined />,
      label: 'إضافة موعد',
      onClick: handleOpenDialog,
      variant: 'contained' as const,
      color: 'primary' as const,
    },
  ], [handleOpenDialog]);

  if (!userProfile?.uid) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* @ts-ignore */}
      <MicrospecDataTable
        key={refreshKey}
        collection="appointments"
        title={{ type: 'Literal', text: 'المواعيد المجدولة' } as any}
        subtitle={{ type: 'Literal', text: 'المواعيد القادمة مع العملاء' } as any}
        columns={columns as any}
        actions={actions as any}
        globalActions={globalActions as any}
        onDataLoad={(data: any[]) => data.filter((row: any) => row.status === 'scheduled')}
        wheres={[{ field: 'owner_id', value: userProfile.uid }]}
        searchable
        refreshable
        emptyMessage="لا توجد مواعيد حتى الآن"
        pageSize={10}
      />

      <AppointmentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={handleApptSuccess}
        clients={clients}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled">{snackMessage}</Alert>
      </Snackbar>
    </Container>
  );
};

export default ManageAppointments;
